import React, { useState } from 'react';
import { Search, UserX, UserCheck, RotateCcw } from 'lucide-react';
import { StatusBadge } from '../../ui/StatusBadge';
import { Input } from '../../ui/input';
import { Button } from '../../ui';

const MOCK_USERS = [
  { id: 1, name: 'Maria Gonzalez',  email: 'mgonzalez@riverside.com',  role: 'Unit Manager',           tenant: 'Riverside Health',   status: 'Active',      lastLogin: '2026-03-16' },
  { id: 2, name: 'James Okafor',    email: 'jokafor@riverside.com',    role: 'Service Line Director',  tenant: 'Riverside Health',   status: 'Active',      lastLogin: '2026-03-15' },
  { id: 3, name: 'Sarah Kim',       email: 'skim@summitcare.com',       role: 'Unit Manager',           tenant: 'Summit Care',        status: 'Invited',     lastLogin: '—' },
  { id: 4, name: 'Devon Patel',     email: 'dpatel@summitcare.com',     role: 'Service Line Director',  tenant: 'Summit Care',        status: 'Active',      lastLogin: '2026-03-14' },
  { id: 5, name: 'Aaliya Noor',     email: 'anoor@valleyhospital.com',  role: 'Unit Manager',           tenant: 'Valley Hospital',    status: 'Deactivated', lastLogin: '2026-01-20' },
  { id: 6, name: 'Tom Bradley',     email: 'tbradley@valleyhospital.com', role: 'Unit Manager',         tenant: 'Valley Hospital',    status: 'Active',      lastLogin: '2026-03-17' },
  { id: 7, name: 'Chen Wei',        email: 'cwei@nexonurse.com',        role: 'System Administrator',   tenant: 'NexoNurse (Internal)', status: 'Active',    lastLogin: '2026-03-17' },
];

export default function UserDirectory() {
  const [users, setUsers] = useState(MOCK_USERS);
  const [search, setSearch] = useState('');
  const [filterTenant, setFilterTenant] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const tenants = [...new Set(MOCK_USERS.map(u => u.tenant))];

  const toggle = (id) => setUsers(us => us.map(u => u.id === id ? { ...u, status: u.status === 'Active' ? 'Deactivated' : 'Active' } : u));
  const resetMfa = (id) => alert(`MFA reset email sent to ${users.find(u => u.id === id)?.email}`);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchQ = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    const matchT = !filterTenant || u.tenant === filterTenant;
    const matchS = !filterStatus || u.status === filterStatus;
    return matchQ && matchT && matchS;
  });

  const statusBadgeVariant = (status) => {
    if (status === 'Active') return 'on-track';
    if (status === 'Invited') return 'watch';
    return 'critical';
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">User Directory</h1>
        <p className="dashboard-subtitle">Manage users across all tenants.</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
            style={{ width: '100%', paddingLeft: 30, boxSizing: 'border-box' }} />
        </div>
        <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)} className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua">
          <option value="">All Tenants</option>
          {tenants.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua">
          <option value="">All Statuses</option>
          {['Active', 'Invited', 'Deactivated'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="p-5" style={{ padding: 0 }}>
          <table className="nn-table">
            <thead>
              <tr>
                {['Name', 'Email', 'Role', 'Tenant', 'Status', 'Last Login', 'Actions'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 500 }}>{u.name}</td>
                  <td className="text-text-muted">{u.email}</td>
                  <td>{u.role}</td>
                  <td className="text-text-muted">{u.tenant}</td>
                  <td>
                    <StatusBadge variant={statusBadgeVariant(u.status)}>{u.status}</StatusBadge>
                  </td>
                  <td className="text-body-sm text-text-muted">{u.lastLogin}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button
                        variant={u.status === 'Active' ? 'destructive' : 'default'}
                        size="sm"
                        onClick={() => toggle(u.id)}
                        title={u.status === 'Active' ? 'Deactivate' : 'Reactivate'}
                      >
                        {u.status === 'Active' ? <UserX size={13} /> : <UserCheck size={13} />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => resetMfa(u.id)} title="Reset MFA">
                        <RotateCcw size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>No users match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} of {users.length} users</div>
    </div>
  );
}
